import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EMPTY, expand, Observable, reduce } from 'rxjs';
import {
  DisbursementBulkHeadCertifyResponse,
  DisbursementCertificationListParams,
  DisbursementCertificationListResponse,
  DisbursementCertificationModel,
  DisbursementDocType,
  DisbursementRequestRevisionPayload,
  DisbursementTestProvisionResponse,
} from '../../models/disbursement/disbursement.model';

// Disbursement Document Certification (ขั้นตอนการรับรองเอกสารส่งเบิก) — v1.2.
// Endpoints mirror apps/api/config/routes.rb exactly. v1.1 removed the whole
// REG subsystem (attach_reg) and the in-system ERP submission step
// (bundle_submit/bulk_bundle_submit) — document officer is view + bundle/zip
// download only; `จนท.สารบัญ` imports PR ERP themselves, outside the system.
// `request_revision` is generalized: teacher/head_of_department/
// finance_officer/vice_dean_administration all send back through the same
// endpoint (the backend dispatches on the caller's active role + the cert's
// current station). v1.2 decision 1 drops the whole disbursement_settings
// surface (createDisbursementSetting/etc. removed — no admin settings screen
// exists any more); the dev-only re-provision trigger moved to
// `POST test_provision` (collection, env-gated server-side). v1.2 decision 4
// adds an on-the-fly per-doc preview endpoint for the approve page, and
// decision 4b adds the สารบัญ per-department ZIP download (C2 backend
// addition, GET department_zip collection route).
@Injectable({
  providedIn: 'root',
})
export class DisbursementCertificationService {
  private apiUrl = '/disbursement_certifications';

  #http = inject(HttpClient);

  getCertifications(
    params?: DisbursementCertificationListParams,
  ): Observable<DisbursementCertificationListResponse> {
    return this.#http.get<DisbursementCertificationListResponse>(this.apiUrl, {
      params: this.buildListParams(params),
    });
  }

  getCertification(id: number): Observable<DisbursementCertificationModel> {
    return this.#http.get<DisbursementCertificationModel>(`${this.apiUrl}/${id}`);
  }

  teacherCertify(id: number): Observable<DisbursementCertificationModel> {
    return this.#http.post<DisbursementCertificationModel>(`${this.apiUrl}/${id}/teacher_certify`, {});
  }

  headCertify(id: number): Observable<DisbursementCertificationModel> {
    return this.#http.post<DisbursementCertificationModel>(`${this.apiUrl}/${id}/head_certify`, {});
  }

  financeApprove(id: number): Observable<DisbursementCertificationModel> {
    return this.#http.post<DisbursementCertificationModel>(`${this.apiUrl}/${id}/finance_approve`, {});
  }

  viceDeanCertify(id: number): Observable<DisbursementCertificationModel> {
    return this.#http.post<DisbursementCertificationModel>(`${this.apiUrl}/${id}/vice_dean_certify`, {});
  }

  // Generalized send-back for whichever of the four certifying actors
  // (teacher/head_of_department/finance_officer/vice_dean_administration) is
  // at the cert's current pending station — dispatched server-side on the
  // caller's active role. Always reason-only.
  requestRevision(
    id: number,
    payload: DisbursementRequestRevisionPayload,
  ): Observable<DisbursementCertificationModel> {
    return this.#http.post<DisbursementCertificationModel>(`${this.apiUrl}/${id}/request_revision`, payload);
  }

  // A1 — vice_dean_academic resolving a `revision_schedule_requested` cert.
  revisionApprove(id: number, comment?: string): Observable<DisbursementCertificationModel> {
    return this.#http.post<DisbursementCertificationModel>(
      `${this.apiUrl}/${id}/revision_approve`,
      comment != null && comment !== '' ? { comment } : {},
    );
  }

  revisionReject(id: number, comment: string): Observable<DisbursementCertificationModel> {
    return this.#http.post<DisbursementCertificationModel>(`${this.apiUrl}/${id}/revision_reject`, { comment });
  }

  // Document officer's per-department bundle download — one certification's
  // active_bundle_document reaches every other cert in the same
  // (term, department) round. Legacy dept-bundle surface; kept alive for the
  // "latest round" download the department_zip endpoint doesn't replace
  // (see api-contract.md).
  downloadBundle(id: number): Observable<Blob> {
    return this.#http.get(`${this.apiUrl}/${id}/bundle`, { responseType: 'blob' });
  }

  // v1.2 §5.1 (สารบัญ block) — streams the per-department Monday ZIP
  // (บันทึกข้อความ.pdf + one merged PDF per completed cert) for a given
  // (term, department). Also covers post-round-4 stragglers, since
  // Pdf::DepartmentZipBuilder only packages certs that already have a
  // persisted `merged` document.
  downloadDepartmentZip(academicTermId: number, departmentId: number): Observable<Blob> {
    const params = new HttpParams()
      .set('academic_term_id', String(academicTermId))
      .set('department_id', String(departmentId));

    return this.#http.get(`${this.apiUrl}/department_zip`, { params, responseType: 'blob' });
  }

  // Persisted final for a completed cert (display page). 404 until the cert
  // reaches pending_bundle (decision 2's display gate enforces this
  // naturally — finals only exist post-completion).
  downloadDocument(id: number, docType: DisbursementDocType): Observable<Blob> {
    return this.#http.get(`${this.apiUrl}/${id}/documents/${docType}`, { responseType: 'blob' });
  }

  // The per-cert merged PDF ("เอกสารรวมพร้อมส่งเบิก") — same endpoint as
  // downloadDocument with doc_type=merged, kept as its own method since every
  // completed cert has exactly one and callers don't need to know the
  // doc_type string.
  downloadMergedDocument(id: number): Observable<Blob> {
    return this.downloadDocument(id, 'merged');
  }

  // v1.2 decision 4 — on-the-fly per-doc preview for the approve page.
  // Renders the generator right now and persists nothing; visible to any
  // actor who can already `show` the certification (mid-chain UV actors
  // included). Reuses the overload-memo blob -> ObjectURL pattern in the
  // component.
  previewDocument(id: number, docType: DisbursementDocType): Observable<Blob> {
    return this.#http.get(`${this.apiUrl}/${id}/documents/${docType}/preview`, { responseType: 'blob' });
  }

  // Walks every page of `getCertifications` and flattens the result. Needed
  // for the display page's สารบัญ block: there is no dedicated aggregation
  // endpoint, so per-department status counts/readiness must be derived
  // client-side from the FULL set of a term's certifications, not just page 1
  // (a single-page fetch would silently under-count once a term exceeds one
  // page).
  getAllCertifications(
    params?: Omit<DisbursementCertificationListParams, 'page' | 'limit'>,
  ): Observable<DisbursementCertificationModel[]> {
    const pageSize = 200;

    const fetchPage = (page: number): Observable<DisbursementCertificationListResponse> => (
      this.getCertifications({ ...params, page, limit: pageSize })
    );

    const expandNextPage = (
      res: DisbursementCertificationListResponse,
    ): Observable<DisbursementCertificationListResponse> => {
      if (res.page < res.total_pages) {
        return fetchPage(res.page + 1);
      }

      return EMPTY;
    };

    const accumulatePages = (
      all: DisbursementCertificationModel[],
      res: DisbursementCertificationListResponse,
    ): DisbursementCertificationModel[] => [...all, ...res.data];

    return fetchPage(1).pipe(
      expand(expandNextPage),
      reduce(accumulatePages, [] as DisbursementCertificationModel[]),
    );
  }

  // §7 collection bulk_head_certify — per-id independent (one failure doesn't
  // roll back the others).
  bulkHeadCertify(ids: number[]): Observable<DisbursementBulkHeadCertifyResponse> {
    return this.#http.post<DisbursementBulkHeadCertifyResponse>(`${this.apiUrl}/bulk_head_certify`, { ids });
  }

  // v1.2 decision 6 — dev-only "เปิดรอบเบิกจ่าย (ทดสอบ)" trigger, relocated from
  // the deleted settings/disbursement page. Env-gated server-side (404
  // outside dev/local, same pattern as the email-login bypass) — the FE only
  // renders the button when environment.enableDisbursementTestTrigger is on.
  testProvision(academicTermId: number): Observable<DisbursementTestProvisionResponse> {
    return this.#http.post<DisbursementTestProvisionResponse>(`${this.apiUrl}/test_provision`, {
      academic_term_id: academicTermId,
    });
  }

  private buildListParams(params?: DisbursementCertificationListParams): HttpParams {
    let httpParams = new HttpParams();
    const supportedParams: (keyof DisbursementCertificationListParams)[] = [
      'academic_term_id',
      'status',
      'scope',
      'department_id',
      'keywords',
      'page',
      'limit',
      'sort',
      'direction',
    ];

    supportedParams.forEach((key) => {
      const value = params?.[key];
      if (value != null && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    });

    return httpParams;
  }
}
